import type { ComboSuggestion } from "@innoclouds/api";
import { defineStore } from "pinia";
import { computed, reactive, ref, toRaw, watch } from "vue";
import { bindSocketStatusReporting } from "#/sdk";
import type { IProduct } from "../types/iCardProduct";
import type { IHealthData } from "../types/iMain";
import clearLocalStorage from "../utils/clearLocalStorage";
import { getCartItemModifiersPaidTotal } from "../utils/freeModifiers";
import { collectMarkingCodes } from "../utils/markingCodes";
import PricePrerender from "../utils/pricePrerender";
import { getWebSocketInstance } from "../utils/WebSocketService";
import { useScheduleStore } from "./schedule";

export const useMainStore = defineStore("mainLib", () => {
	const schedule = useScheduleStore();
	const toggleDrum = ref<boolean>(false);
	const kiosk_timeout = ref<any | null>(localStorage.getItem("kiosk_timeout"));
	const kiosk_FirstTimeout = ref<any>(0);
	const is_18_above_content = ref<boolean>(false);
	const is18Allowed = ref<boolean>(sessionStorage.getItem("is_adult") === "true");
	const cartProducts_lib = ref<IProduct[]>([]);
	const cartMarkingCodes = computed(() => new Set(cartProducts_lib.value.flatMap((item) => collectMarkingCodes(item))));
	const activeScrolling_lib = ref<HTMLElement[]>([]);
	const isOrderTypeSubmited = ref(false);

	const config_lib = ref();
	const xApiKey_lib = ref("");

	const countBonus = ref(0);
	const openApiKey = ref<boolean>(false);

	const is_OpenLoyal = ref<boolean>(false);
	const isLoyalSuccessScreen = ref<boolean>(false);
	const healthState = ref<boolean>(true);
	const healthData = ref<IHealthData>();
	const is_showApiKey = ref(!localStorage.getItem("apiKey"));
	const is_notification_lib = ref(false);
	const notificationData_lib = ref<{ textNotification: string; fastAdd?: any; callback?: any }>({ textNotification: "" });
	const displayCards = ref<any[]>([]);
	const combos = ref<ComboSuggestion[]>([]);

	/** Инкремент при каждом addToCart — для анимации кнопки корзины (BasketButton) */
	const cartBasketBumpKey = ref(0);

	const cartAddInterceptor = ref<((item: any) => boolean) | null>(null);

	const cartRemoveInterceptor = ref<((item: any) => boolean) | null>(null);

	const modifierMarkRequester = ref<((modifier: any, usedCodes?: string[]) => Promise<string | null>) | null>(null);

	/** Строка, которую сканер присылает вместо GS в DataMatrix (device.marking_gs_substitute); "" — без замены */
	const markingGsSubstitute = ref("");

	let ws: any | null = null;
	let wsUnsubscribers: Array<() => void> = [];

	const disposeHeartbeatWebSocket = () => {
		for (const unsub of wsUnsubscribers) unsub();
		wsUnsubscribers = [];
		if (ws) {
			ws.close();
			ws = null;
		}
	};

	const checkHeartbeatApp = (id: any) => {
		disposeHeartbeatWebSocket();

		const wsUrl = `${import.meta.env.VITE_WS_URL}/ws/device/${id}/`;
		ws = getWebSocketInstance(wsUrl, { heartbeatTimeout: 60_000 });

		wsUnsubscribers = [
			bindSocketStatusReporting(ws, "device", wsUrl),
			ws.on("open", () => console.debug("Heartbeat WebSocket подключён (deviceId:", id, ")")),
			ws.on("close", () => console.debug("Heartbeat WebSocket закрыт")),
			ws.on("error", (error: unknown) => console.warn("Heartbeat WebSocket error:", error)),
			ws.on("message", (data: any) => {
				if (data.error) {
					is_notification_lib.value = true;
					notificationData_lib.value.textNotification = data.error;
					return;
				}

				if (!data?.params) return;

				healthData.value = data.params as IHealthData;
				healthState.value = data.params.is_ok === true;
			}),
		];

		ws.connect();
	};

	const setKioskTimeout = <H extends number | string>(timeout: H) => {
		kiosk_FirstTimeout.value = timeout;
		localStorage.setItem("kiosk_timeout", timeout as string);
	};

	const setStateDrum = (toggle: boolean) => {
		toggleDrum.value = toggle;
	};

	const totalPriceServer = ref<number | null>(null);

	const initialCartPrice_lib = computed(() =>
		cartProducts_lib.value.reduce((sum: any, item: any) => {
			const modifiersPrice = getCartItemModifiersPaidTotal(item, "old_price") * (item.amount ?? 1);
			return sum + item.old_price * item.amount + modifiersPrice;
		}, 0),
	);

	const totalPrice_lib = computed({
		get: () => {
			if (cartProducts_lib.value.length === 0 || totalPriceServer.value === 0) return 0;

			if (totalPriceServer.value && totalPriceServer.value !== 0) return totalPriceServer.value;

			return cartProducts_lib.value.reduce((sum: any, item: any) => {
				const price = PricePrerender(item) * item.amount;
				const modifiersPrice = getCartItemModifiersPaidTotal(item) * (item.amount ?? 1);
				return sum + price + modifiersPrice;
			}, 0);
		},
		set(v) {
			totalPriceServer.value = v;
		},
	});

	const setBonus = (value: number) => {
		countBonus.value = value;
		localStorage.setItem("bonus", value.toString());
	};

	const removeFromLocalStorage = (elements: any[]) => {
		clearLocalStorage(elements);
	};

	const addToCart = (item: any) => {
		if (schedule.isClosed && !schedule.isNight) return schedule.notify();

		if (cartAddInterceptor.value && cartAddInterceptor.value(item)) return;

		if (collectMarkingCodes(item).some((code) => cartMarkingCodes.value.has(code))) return;

		item = { ...item, idSystem: crypto.randomUUID() };

		const reactiveProduct = makeProductFullyReactive(item);
		cartProducts_lib.value.push(reactiveProduct);

		updateCartInLocalStorage();
		cartBasketBumpKey.value += 1;
	};

	const updateCartInLocalStorage = (): void => {
		try {
			if (!cartProducts_lib.value || cartProducts_lib.value.length === 0) {
				localStorage.removeItem("cart");
				return;
			}

			localStorage.setItem("cart", JSON.stringify(toRaw(cartProducts_lib.value)));
		} catch (error) {
			console.error("Failed to update cart in localStorage:", error);
		}
	};

	const makeProductFullyReactive = (item: any): IProduct => {
		const result: any = {
			...item,
			amount: item.amount ?? 1,
			price: item.old_price,
			new_price: item.new_price !== undefined ? item.new_price : null,
			discounts: item.discounts ?? [],
			modifiers: (item.modifiers || []).map((modifier: any) =>
				reactive({
					...modifier,
					discounts: [],
					price: modifier.old_price,
					new_price: null,
					total_new_price: null,
					delta_price: null,
				}),
			),
			group_modifiers: (item.group_modifiers || []).map((group: any) =>
				reactive({
					...group,
					modifiers: (group.modifiers || []).map((modifier: any) =>
						reactive({
							...modifier,
							discounts: [],
							price: modifier.old_price,
							new_price: null,
							total_new_price: null,
							delta_price: null,
						}),
					),
				}),
			),
		};

		if (item.total_new_price !== undefined) {
			result.total_new_price = item.total_new_price;
		} else {
			result.total_new_price = null;
		}

		return reactive(result);
	};

	const plusAmount = (idSystem: string) => {
		const item = cartProducts_lib.value.find((item) => item.idSystem === idSystem);
		if (item) {
			item.amount += 1;
		}
		updateCartInLocalStorage();
	};

	const minusAmount = (idSystem: string) => {
		const item = cartProducts_lib.value.find((item) => item.idSystem === idSystem);
		if (!item) return;

		if (item.amount >= 1) {
			item.amount -= 1;
		}
		updateCartInLocalStorage();
	};

	const loadCart = () => {
		try {
			const saved = localStorage.getItem("cart");
			if (saved) {
				const parsed = JSON.parse(saved);
				cartProducts_lib.value.splice(0, cartProducts_lib.value.length, ...parsed.map(makeProductFullyReactive));
				totalPriceServer.value = null;
			}
		} catch (error) {
			console.error("Failed to load cart from localStorage:", error);
		}
	};
	loadCart();

	const orderComment = ref<string>(localStorage.getItem("cart_comment") || "");

	const setOrderComment = (value: string): void => {
		orderComment.value = value;
		try {
			if (value.length) localStorage.setItem("cart_comment", value);
			else localStorage.removeItem("cart_comment");
		} catch (error) {
			console.error("Failed to update cart comment in localStorage:", error);
		}
	};

	watch(
		() => cartProducts_lib.value.length,
		(length) => {
			if (length === 0 && orderComment.value.length) setOrderComment("");
		},
		{ immediate: true },
	);

	// Удаление товара из корзины
	const deleteItemFromCart = (idSystem?: string) => {
		if (idSystem) {
			const index = cartProducts_lib.value.findIndex((item) => item.idSystem === idSystem);
			if (index === -1) return;
			cartProducts_lib.value.splice(index, 1);
			updateCartInLocalStorage();
			totalPriceServer.value = null;
		} else {
			cartProducts_lib.value = [];
			updateCartInLocalStorage();
		}
	};

	return {
		cartProducts_lib,
		activeScrolling_lib,
		config_lib,
		totalPrice_lib,
		initialCartPrice_lib,
		is_notification_lib,
		notificationData_lib,
		xApiKey_lib,
		countBonus,
		toggleDrum,
		is_showApiKey,
		openApiKey,
		kiosk_timeout,
		kiosk_FirstTimeout,
		is_OpenLoyal,
		isLoyalSuccessScreen,
		is_18_above_content,
		is18Allowed,
		healthState,
		healthData,
		displayCards,
		combos,
		isOrderTypeSubmited,
		cartBasketBumpKey,
		cartAddInterceptor,
		cartRemoveInterceptor,
		modifierMarkRequester,
		cartMarkingCodes,
		markingGsSubstitute,
		orderComment,
		setOrderComment,
		schedule,
		removeFromLocalStorage,
		setKioskTimeout,
		setBonus,
		setStateDrum,
		addToCart,
		plusAmount,
		minusAmount,
		loadCart,
		deleteItemFromCart,
		updateCartInLocalStorage,
		makeProductFullyReactive,
		checkHeartbeatApp,
	};
});
